import { useCallback, useEffect, useRef, useState } from 'react';
import { getClipboardImageFiles, getImageFiles } from './imageInput';
import { useConfirm } from '../components/ui';

/* ---------- Unsaved-changes guard ---------- */

export const DISCARD_CHANGES_PROMPT = {
  tone: 'danger',
  title: 'Discard changes?',
  description: "You have changes that haven't been saved. If you leave now, they will be lost.",
  confirmLabel: 'Discard',
  cancelLabel: 'Keep editing',
};

/**
 * While the form has unsaved changes, closing the tab asks first. The returned
 * `leaveIfConfirmed(action)` runs an in-app close/navigation right away when nothing
 * changed, and otherwise only after the user chooses Discard in the in-app dialog.
 */
export function useUnsavedChangesGuard(isDirty) {
  const confirm = useConfirm();
  const dirtyRef = useRef(isDirty);

  useEffect(() => {
    dirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      // Older browsers need returnValue set to show the prompt.
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  return useCallback((action) => {
    if (!dirtyRef.current) {
      action();
      return Promise.resolve(true);
    }
    return Promise.resolve(confirm(DISCARD_CHANGES_PROMPT)).then((discard) => {
      if (discard) action();
      return Boolean(discard);
    });
  }, [confirm]);
}

/* ---------- Remembered collapsible sections ---------- */

export const SECTION_OPEN_STORAGE_PREFIX = 'bandz-entry-section-open';

export function getSectionOpenStorageKey(sectionKey, uid) {
  return `${SECTION_OPEN_STORAGE_PREFIX}:${uid || 'signed-out'}:${sectionKey}`;
}

function readSectionOpen(storageKey, fallback) {
  try {
    const saved = localStorage.getItem(storageKey);
    if (saved === 'true') return true;
    if (saved === 'false') return false;
  } catch (_) {
    // Storage can be blocked (private mode); the default is fine.
  }
  return fallback;
}

function writeSectionOpen(storageKey, open) {
  try {
    localStorage.setItem(storageKey, open ? 'true' : 'false');
  } catch (_) {
    // Remembering a section is only a convenience.
  }
}

/** Open/closed state for a collapsible form section, remembered per user on this device. */
export function useRememberedSectionOpen(sectionKey, defaultOpen, uid) {
  const storageKey = getSectionOpenStorageKey(sectionKey, uid);
  const [isOpen, setIsOpen] = useState(() => readSectionOpen(storageKey, defaultOpen));

  // The signed-in user can arrive after the first render.
  useEffect(() => {
    setIsOpen(readSectionOpen(storageKey, defaultOpen));
  }, [storageKey, defaultOpen]);

  const toggle = useCallback(() => {
    setIsOpen((current) => {
      const next = !current;
      writeSectionOpen(storageKey, next);
      return next;
    });
  }, [storageKey]);

  return [isOpen, toggle];
}

/* ---------- Chart drag and drop ---------- */

export const CHART_DROP_HIGHLIGHT_STYLE = {
  boxShadow: '0 0 0 2px var(--theme-accent)',
  backgroundColor: 'var(--theme-accent-soft)',
};

/** For a chart area without its own border: a soft halo so the content keeps breathing room. */
export const CHART_DROP_HIGHLIGHT_HALO_STYLE = {
  boxShadow: '0 0 0 6px var(--theme-accent-soft), 0 0 0 8px var(--theme-accent)',
  backgroundColor: 'var(--theme-accent-soft)',
};

const isFileDrag = (event) => Array.from(event?.dataTransfer?.types || []).includes('Files');

/**
 * Drop-zone props for the chart area plus a dragging flag for a subtle highlight.
 * While mounted, files dropped outside the zone are ignored instead of making the
 * browser open the image and leave the form.
 */
export function useChartDropZone(onFiles) {
  const [isDragging, setIsDragging] = useState(false);
  const depthRef = useRef(0);
  const onFilesRef = useRef(onFiles);

  useEffect(() => {
    onFilesRef.current = onFiles;
  }, [onFiles]);

  useEffect(() => {
    const reset = () => {
      depthRef.current = 0;
      setIsDragging(false);
    };
    const blockStrayDragOver = (event) => {
      if (!isFileDrag(event) || event.defaultPrevented) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'none';
    };
    const blockStrayDrop = (event) => {
      if (isFileDrag(event) && !event.defaultPrevented) event.preventDefault();
      reset();
    };
    window.addEventListener('dragover', blockStrayDragOver);
    window.addEventListener('drop', blockStrayDrop);
    window.addEventListener('dragend', reset);
    return () => {
      window.removeEventListener('dragover', blockStrayDragOver);
      window.removeEventListener('drop', blockStrayDrop);
      window.removeEventListener('dragend', reset);
    };
  }, []);

  const onDragEnter = useCallback((event) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    depthRef.current += 1;
    setIsDragging(true);
  }, []);

  const onDragOver = useCallback((event) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback((event) => {
    if (!isFileDrag(event)) return;
    depthRef.current = Math.max(0, depthRef.current - 1);
    if (depthRef.current === 0) setIsDragging(false);
  }, []);

  const onDrop = useCallback((event) => {
    event.preventDefault();
    depthRef.current = 0;
    setIsDragging(false);
    const files = getImageFiles(event.dataTransfer?.files);
    if (files.length > 0) onFilesRef.current(files);
  }, []);

  return { isDragging, dropZoneProps: { onDragEnter, onDragOver, onDragLeave, onDrop } };
}

/* ---------- Paste a screenshot anywhere in the form ---------- */

const isTextEntryTarget = (target) => {
  if (!target || typeof target.closest !== 'function') return false;
  if (target.isContentEditable) return true;
  const field = target.closest('input, textarea');
  if (!field) return false;
  if (field.tagName === 'TEXTAREA') return true;
  return !['button', 'checkbox', 'radio', 'file', 'submit', 'reset', 'image', 'range', 'color'].includes(
    (field.getAttribute('type') || 'text').toLowerCase()
  );
};

const clipboardHasText = (event) => {
  try {
    return Boolean(event.clipboardData?.getData?.('text/plain'));
  } catch (_) {
    return false;
  }
};

/**
 * Ctrl/Cmd+V with an image on the clipboard adds it as a chart while the form is open.
 * Text pastes into a field are left alone; image-only pastes are taken anywhere.
 */
export function useDocumentImagePaste(onFiles, enabled = true) {
  const onFilesRef = useRef(onFiles);

  useEffect(() => {
    onFilesRef.current = onFiles;
  }, [onFiles]);

  useEffect(() => {
    if (!enabled) return undefined;
    const handlePaste = (event) => {
      // A chart area's own paste handler already took it.
      if (event.defaultPrevented) return;
      const files = getClipboardImageFiles(event);
      if (files.length === 0) return;
      if (isTextEntryTarget(event.target) && clipboardHasText(event)) return;
      event.preventDefault();
      onFilesRef.current(files);
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [enabled]);
}

/* ---------- Ctrl/Cmd+Enter saves ---------- */

/**
 * Ctrl/Cmd+Enter clicks the form's Save button, exactly like the user would:
 * nothing happens while it is disabled (saving, or required values missing),
 * and a held key does not save twice.
 */
export function useSubmitShortcut(formRef) {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== 'Enter' || !(event.ctrlKey || event.metaKey)) return;
      if (event.altKey || event.shiftKey || event.isComposing || event.defaultPrevented) return;
      const form = formRef.current;
      if (!form) return;
      const { target } = event;
      const fromPage = !target || target === document.body || target === document.documentElement;
      if (!fromPage && !form.contains(target)) return;
      event.preventDefault();
      if (event.repeat) return;
      const submitButton = form.querySelector('button[type="submit"]');
      if (!submitButton || submitButton.disabled) return;
      submitButton.click();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [formRef]);
}
